import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button as MuiButton,
  Grid,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DeleteOutline as DeleteOutlineIcon,
  Edit as EditIcon,
  InfoOutlined as InfoOutlinedIcon,
  LocalShipping as LocalShippingIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import * as Styled from "./styles";
import Button from "components/atoms/Button";
import { useAppDispatch } from "store/hooks";
import { setIsSpinnerVisible } from "store/modules/AppConfig/index.store";
import i18next from "i18next";
import { toast } from "utils/toast";
import { CoverageAreaProps } from "store/modules/Logistics/types.store";
import { patchCoverageAreaDrivers } from "store/modules/Logistics/index.store";
import { DealerDriverDto } from "types/dealerDrivers";
import {
  DriverOption,
  useMapAreaDriverSelection,
} from "./hooks/useMapAreaDriverSelection";
import { MapDriversEditDialog } from "./MapDriversEditDialog";
import { mapAreaTextLinkButtonSx } from "./mapAreaTextLinkButtonSx";
import {
  deliveryPeopleInfoIconSx,
  getDeliveryPeoplePriorityTooltipComponentsProps,
  getMapAreaSlotDeleteIconButtonSx,
  mapAreaAddAnotherDriverRowSx,
  mapAreaEditDriversStackSx,
  mapAreaSlotAutocompleteListboxProps,
  mapAreaSlotAutocompleteSx,
  mapAreaViewEditButtonSx,
  nonMapAreaDriverAutocompleteSx,
  selectDeliveryEditButtonSx,
} from "./muiSx";
import { buildMapAreaDriversStatePatch } from "./buildCoverageAreaDriversStatePatch";
import { saveCoverageAreaDrivers } from "./putCoverageAreaDrivers";
import {
  assignedDriversToDriverOptions,
  getCoverageAreaAssignedDriversForDisplay,
  getDriversFromCoverageAreaField,
  hasCoverageAreaDriversForDisplay,
} from "./coverageAreaAssignedDrivers";
import { canManageCoverageAreaDriver } from "utils/coverageAreaDriverRules";
import { isDealerMapType, isDriverStoreType } from "types/coverageAreaType";

const MAX_DRIVERS_PER_MAP_COVERAGE_AREA = 3;

interface CardFluidHeaderProps {
  data?: unknown;
  dealerSelected: string;
  dealerId?: string;
  dealerDrivers?: DealerDriverDto[];
  currentCoverageArea: CoverageAreaProps;
}

function upperLabel(value: string | undefined): string {
  return (value ?? "").toUpperCase();
}

function coverageAreaDriverDisplayLabel(
  matchedDriver: DealerDriverDto | undefined,
  detailsName: string,
  detailsSurname?: string,
): string {
  if (matchedDriver?.name) {
    return matchedDriver.name.trim();
  }
  const name = String(detailsName ?? "").trim();
  const surname = String(detailsSurname ?? "").trim();
  return surname ? `${name} ${surname}`.trim() : name;
}

function CardDriverArea({
  dealerSelected,
  dealerId: dealerIdProp,
  dealerDrivers,
  currentCoverageArea,
}: CardFluidHeaderProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [addDrive, setAddDrive] = useState<boolean>(false);
  const [mapDriversCancelModalOpen, setMapDriversCancelModalOpen] =
    useState(false);
  const [mapDriversSaveModalOpen, setMapDriversSaveModalOpen] = useState(false);
  const [driveSelected, setDriveSelected] = useState<DriverOption>();
  const {
    slotRows,
    resetToDrivers,
    selectedDrivers,
    setSlotValue,
    addEmptySlot,
  } = useMapAreaDriverSelection();

  const dispatch = useAppDispatch();
  const dealerId = dealerIdProp || currentCoverageArea?.dealer?.id || "";
  const allDrivers = dealerDrivers;

  useEffect(() => {
    setAddDrive(false);
    setDriveSelected(undefined);
    resetToDrivers([]);
    setMapDriversCancelModalOpen(false);
    setMapDriversSaveModalOpen(false);
  }, [dealerSelected, currentCoverageArea.id, resetToDrivers]);

  const assignedDriversForDisplay = useMemo(
    () => getCoverageAreaAssignedDriversForDisplay(currentCoverageArea),
    [currentCoverageArea],
  );

  const mapAreaDriversFromField = useMemo(
    () => getDriversFromCoverageAreaField(currentCoverageArea),
    [currentCoverageArea],
  );

  const hasAreaDriversAssigned = assignedDriversForDisplay.length > 0;

  const canManageAreaDriver = canManageCoverageAreaDriver(
    currentCoverageArea?.type,
  );

  const IUsers = useMemo(() => {
    return allDrivers?.map((driver) => ({
      label: (driver.name ?? "").trim(),
      id: driver.id,
    }));
  }, [allDrivers]);

  const hasMultipleDriversRegistered = (IUsers?.length ?? 0) > 1;

  const optionsForSlot = useCallback(
    (rowKey: string) => {
      if (!IUsers?.length) {
        return [];
      }
      const takenIds = slotRows
        .map((row) => (row.key !== rowKey && row.value ? row.value.id : null))
        .filter((id): id is string => id !== null);
      const taken = new Set(takenIds);
      return IUsers.filter((option) => !taken.has(option.id));
    },
    [IUsers, slotRows],
  );

  const maxDriverSlotsForMapArea = Math.min(
    MAX_DRIVERS_PER_MAP_COVERAGE_AREA,
    IUsers?.length ?? 0,
  );

  const canAddAnotherDriverRow =
    hasMultipleDriversRegistered && slotRows.length < maxDriverSlotsForMapArea;

  const isLastDriverSlotFilled = useMemo(() => {
    if (slotRows.length === 0) {
      return false;
    }
    return slotRows[slotRows.length - 1].value !== null;
  }, [slotRows]);

  const isAddDriverActionDisabled =
    !canAddAnotherDriverRow || !isLastDriverSlotFilled;

  const openDriverSelection = useCallback(() => {
    if (!dealerId) {
      return;
    }

    const driversList = allDrivers ?? [];
    const driversForEdit =
      getCoverageAreaAssignedDriversForDisplay(currentCoverageArea);
    const driverOptions = assignedDriversToDriverOptions(
      driversForEdit,
      driversList,
    );

    if (isDealerMapType(currentCoverageArea?.type)) {
      resetToDrivers(driverOptions);
    } else {
      setDriveSelected(driverOptions[0] ?? undefined);
    }

    setAddDrive(true);
  }, [allDrivers, currentCoverageArea, dealerId, resetToDrivers]);

  const persistCoverageAreaDrivers = useCallback(
    async (driversToPersist: DriverOption[]) => {
      if (!currentCoverageArea?.dealer?.id || !currentCoverageArea.id) {
        return;
      }

      await saveCoverageAreaDrivers({
        dealerId: currentCoverageArea.dealer.id,
        coverageAreaId: currentCoverageArea.id,
        driversToPersist,
      });

      dispatch(
        patchCoverageAreaDrivers({
          coverageAreaId: currentCoverageArea.id,
          patch: buildMapAreaDriversStatePatch({
            dealerId: currentCoverageArea.dealer.id,
            driversToPersist,
            drivers: allDrivers,
          }),
        }),
      );
    },
    [allDrivers, currentCoverageArea, dispatch],
  );

  const handleAddDrive = useCallback(async () => {
    if (!addDrive || !driveSelected || !currentCoverageArea?.dealer?.id) {
      return;
    }

    try {
      dispatch(setIsSpinnerVisible(true));
      await persistCoverageAreaDrivers([driveSelected]);
      toast(String(t("cardDriverEditSaveSuccess")), { type: "success" });
      dispatch(setIsSpinnerVisible(false));
      setAddDrive(false);
    } catch (error) {
      dispatch(setIsSpinnerVisible(false));
      toast(String(t("cardDriverEditSaveError")), { type: "error" });
    }
  }, [
    addDrive,
    currentCoverageArea?.dealer?.id,
    dispatch,
    driveSelected,
    persistCoverageAreaDrivers,
    t,
  ]);

  const handleSaveMapAreaDrivers = useCallback(async () => {
    if (!currentCoverageArea?.dealer?.id) {
      return;
    }

    try {
      dispatch(setIsSpinnerVisible(true));

      const driversToPersist = selectedDrivers.slice(
        0,
        MAX_DRIVERS_PER_MAP_COVERAGE_AREA,
      );

      await persistCoverageAreaDrivers(driversToPersist);

      toast(String(t("cardDriverEditSaveSuccess")), { type: "success" });
      dispatch(setIsSpinnerVisible(false));
      setAddDrive(false);
    } catch (error) {
      dispatch(setIsSpinnerVisible(false));
      toast(String(t("cardDriverEditSaveError")), { type: "error" });
    }
  }, [
    currentCoverageArea?.dealer?.id,
    dispatch,
    persistCoverageAreaDrivers,
    selectedDrivers,
    t,
  ]);

  const handleRemoveDrive = useCallback(async () => {
    if (!addDrive || !currentCoverageArea?.dealer?.id) {
      return;
    }

    try {
      dispatch(setIsSpinnerVisible(true));
      await persistCoverageAreaDrivers([]);
      setAddDrive(false);
      dispatch(setIsSpinnerVisible(false));
      toast(i18next.t("Motorista removido").toString(), {
        type: "success",
      });
    } catch (error) {
      dispatch(setIsSpinnerVisible(false));
      toast(i18next.t("Falha ao tentar remover motorista").toString(), {
        type: "error",
      });
    }
  }, [
    addDrive,
    currentCoverageArea?.dealer?.id,
    dispatch,
    persistCoverageAreaDrivers,
  ]);

  const isMapArea = isDealerMapType(currentCoverageArea?.type);

  const hasMapAreaDriversAssigned = isMapArea && hasAreaDriversAssigned;

  const showMapAreaMultiDriverList =
    isMapArea && mapAreaDriversFromField.length > 1;

  const shouldShowMapDriverConfirmModals = isMapArea;

  useEffect(() => {
    if (!isMapArea) {
      setMapDriversCancelModalOpen(false);
      setMapDriversSaveModalOpen(false);
    }
  }, [isMapArea]);

  const selectDeliveryPrimary = isMapArea
    ? !hasMapAreaDriversAssigned
    : !hasAreaDriversAssigned;

  const mapAreaActionLabel = hasMapAreaDriversAssigned
    ? t("editDeliveryPeople")
    : t("selectDeliveryPeople");

  const selectDeliveryLabel = isMapArea
    ? mapAreaActionLabel
    : t("selectDeliveryPerson");

  const mapAreaSectionTitle = hasMapAreaDriversAssigned
    ? t("defineDeliveryPeopleSectionTitle")
    : t("selectDeliveryPeopleSectionTitle");

  const deliveryPersonNameLabel = useMemo(() => {
    const primaryDriver = assignedDriversForDisplay[0];
    if (!primaryDriver?.details?.id) {
      return undefined;
    }

    const matched = allDrivers?.find(
      (driver) => driver.id === primaryDriver.details.id,
    );
    return coverageAreaDriverDisplayLabel(
      matched,
      primaryDriver.details.name ?? "",
      primaryDriver.details.surname,
    );
  }, [allDrivers, assignedDriversForDisplay]);

  return (
    <>
      <Styled.Container
        $hasAreaDriverAssigned={hasAreaDriversAssigned || isMapArea}
      >
        <Styled.TopContainer>
          {!isMapArea &&
            !hasCoverageAreaDriversForDisplay(currentCoverageArea) && (
              <WarningIcon fontSize="small" color="error" />
            )}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            flexWrap="wrap"
          >
            <Typography variant="body1" fontWeight={600}>
              {isMapArea ? mapAreaSectionTitle : t("assignADelivery")}
            </Typography>
            {isMapArea && (
              <Tooltip
                title={t("deliveryPeoplePriorityTooltip")}
                placement="top"
                enterTouchDelay={0}
                componentsProps={getDeliveryPeoplePriorityTooltipComponentsProps(
                  theme,
                )}
              >
                <Styled.InfoTooltipTrigger>
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={deliveryPeopleInfoIconSx}
                    aria-label={String(t("deliveryPeoplePriorityAriaLabel"))}
                  />
                </Styled.InfoTooltipTrigger>
              </Tooltip>
            )}
          </Stack>
        </Styled.TopContainer>

        <Styled.ActionsContainer editMode={false}>
          {!addDrive && showMapAreaMultiDriverList && (
            <Styled.MapAreaViewContainer>
              <Styled.DriversListContainer>
                {mapAreaDriversFromField
                  .filter((driverItem) => Boolean(driverItem.details?.id))
                  .map((driverItem, index) => (
                    <Styled.DriverListRow key={driverItem.id || index}>
                      <LocalShippingIcon fontSize="small" color="inherit" />
                      <Styled.DriverListRowText>
                        <Typography variant="caption" textAlign="start">
                          {t("deliveryPersonSlotLabel", { number: index + 1 })}
                        </Typography>
                        <Typography variant="subtitle2" textAlign="start">
                          {coverageAreaDriverDisplayLabel(
                            allDrivers?.find(
                              (driver) => driver.id === driverItem.details.id,
                            ),
                            driverItem.details.name ?? "",
                            driverItem.details.surname,
                          )}
                        </Typography>
                      </Styled.DriverListRowText>
                    </Styled.DriverListRow>
                  ))}
              </Styled.DriversListContainer>
              <Button
                sx={mapAreaViewEditButtonSx}
                variant="contained"
                endIcon={<EditIcon />}
                onClick={() => {
                  openDriverSelection();
                }}
              >
                {upperLabel(t("editDeliveryPeople"))}
              </Button>
            </Styled.MapAreaViewContainer>
          )}
          {!addDrive && !showMapAreaMultiDriverList && (
            <Styled.ContainerDrive>
              <Styled.ContainerDriverSelected>
                <LocalShippingIcon fontSize="small" color="inherit" />
                <Styled.ContainerText>
                  <Typography variant="caption" textAlign="start">
                    {t("deliveryman")}
                  </Typography>
                  <Typography variant="subtitle2" textAlign="start">
                    {deliveryPersonNameLabel ?? t("undefined")}
                  </Typography>
                </Styled.ContainerText>
              </Styled.ContainerDriverSelected>

              {canManageAreaDriver && (
                <Button
                  sx={selectDeliveryEditButtonSx}
                  variant={selectDeliveryPrimary ? "contained" : "outlined"}
                  endIcon={<EditIcon />}
                  onClick={() => {
                    openDriverSelection();
                  }}
                >
                  {upperLabel(selectDeliveryLabel)}
                </Button>
              )}
            </Styled.ContainerDrive>
          )}
          {addDrive && isMapArea && (
            <Styled.MapAreaEditColumn>
              <Typography variant="body2" color="text.secondary" component="p">
                {t("addDriversForCoverageInstruction")}
              </Typography>
              <Stack spacing={2} sx={mapAreaEditDriversStackSx}>
                {slotRows.map((row, index) => (
                  <Styled.MapDriverSelectRow key={row.key}>
                    <Autocomplete<DriverOption, false, false, false>
                      disablePortal
                      options={optionsForSlot(row.key)}
                      size="small"
                      value={row.value}
                      onChange={(_, option) => setSlotValue(row.key, option)}
                      ListboxProps={mapAreaSlotAutocompleteListboxProps}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={
                            row.value
                              ? t("deliveryPersonSlotLabel", {
                                  number: index + 1,
                                })
                              : t("selectDriver")
                          }
                        />
                      )}
                      sx={mapAreaSlotAutocompleteSx}
                    />
                    <IconButton
                      disableRipple
                      size="small"
                      onClick={() => setSlotValue(row.key, null)}
                      aria-label={t("delete")}
                      sx={getMapAreaSlotDeleteIconButtonSx(Boolean(row.value))}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Styled.MapDriverSelectRow>
                ))}
              </Stack>
              {hasMultipleDriversRegistered && (
                <Box sx={mapAreaAddAnotherDriverRowSx}>
                  <MuiButton
                    variant="text"
                    disabled={isAddDriverActionDisabled}
                    onClick={addEmptySlot}
                    sx={mapAreaTextLinkButtonSx}
                  >
                    {t("addAnotherDriver")}
                  </MuiButton>
                </Box>
              )}
              <Styled.MapAreaButtonsRow>
                <Button
                  color="inherit"
                  variant="outlined"
                  type="button"
                  onClick={() => setMapDriversCancelModalOpen(true)}
                >
                  <Typography variant="button" textAlign="start">
                    {upperLabel(t("cancel"))}
                  </Typography>
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  type="button"
                  onClick={() => setMapDriversSaveModalOpen(true)}
                >
                  <Typography variant="button" textAlign="start">
                    {upperLabel(t("save"))}
                  </Typography>
                </Button>
              </Styled.MapAreaButtonsRow>
            </Styled.MapAreaEditColumn>
          )}
          {addDrive && !isMapArea && (
            <Styled.ContainerAutoComplete>
              <Autocomplete
                disablePortal
                id="combo-box-demo"
                options={IUsers ?? []}
                size="small"
                value={driveSelected ?? null}
                onChange={(_, option) => setDriveSelected(option ?? undefined)}
                renderInput={(params) => (
                  <TextField {...params} label={t("selectDriver")} />
                )}
                sx={nonMapAreaDriverAutocompleteSx}
              />

              <Grid>
                <Styled.ContainerButtonsDrive>
                  <Button
                    color="inherit"
                    variant="outlined"
                    type="button"
                    onClick={() => setAddDrive(false)}
                  >
                    <Typography variant="button" textAlign="start">
                      {upperLabel(t("exit"))}
                    </Typography>
                  </Button>
                  <Button
                    color="error"
                    variant="contained"
                    type="button"
                    disabled={
                      isDriverStoreType(currentCoverageArea.type) &&
                      !driveSelected?.label.length
                    }
                    onClick={() =>
                      driveSelected?.label?.length
                        ? handleAddDrive()
                        : handleRemoveDrive()
                    }
                  >
                    {upperLabel(t("toApply"))}
                  </Button>
                </Styled.ContainerButtonsDrive>
              </Grid>
            </Styled.ContainerAutoComplete>
          )}
        </Styled.ActionsContainer>
      </Styled.Container>
      <MapDriversEditDialog
        open={shouldShowMapDriverConfirmModals && mapDriversCancelModalOpen}
        onClose={() => setMapDriversCancelModalOpen(false)}
        title={t("cancel")}
        message={t("mapDriversCancelModalMessage")}
        dismissLabel={t("continueEditing")}
        confirmLabel={t("mapDriversCancelModalConfirm")}
        onConfirm={() => {
          setMapDriversCancelModalOpen(false);
          setAddDrive(false);
        }}
      />
      <MapDriversEditDialog
        open={shouldShowMapDriverConfirmModals && mapDriversSaveModalOpen}
        onClose={() => setMapDriversSaveModalOpen(false)}
        title={t("save")}
        message={t("mapDriversSaveModalMessage")}
        confirmLabel={t("mapDriversSaveModalConfirm")}
        onConfirm={async () => {
          setMapDriversSaveModalOpen(false);
          await handleSaveMapAreaDrivers();
        }}
      />
    </>
  );
}

export default CardDriverArea;
